import '../i18n/autoTranslate';
import React, { useEffect, useMemo, useRef } from "react";
import { Sparkles, Send, Loader2, Square, CheckCircle2, Target, Brain, MessageSquare, ArrowRight, CircleDot } from "lucide-react";



function cleanMessageText(value) {
  if (value == null) return "";
  if (typeof value === "object") {
    const feedback = value.feedback || value.summary || value.message || "";
    const followUp = value.followUp || value.follow_up || value.next_question || "";
    const improvements = Array.isArray(value.improvements) ? value.improvements : [];
    return [
      feedback ? `✅ Code review\n${feedback}` : "",
      improvements.length ? `🎯 Improve next\n${improvements.map((x) => `• ${x}`).join("\n")}` : "",
      followUp ? `🧠 Follow-up question\n${followUp}` : "",
    ].filter(Boolean).join("\n\n");
  }
  let text = String(value || "").trim();
  try {
    const parsed = JSON.parse(text);
    if (parsed && typeof parsed === "object") return cleanMessageText(parsed);
  } catch {}
  const start = text.indexOf("{");
  const end = text.lastIndexOf("}");
  if (start >= 0 && end > start) {
    try {
      const parsed = JSON.parse(text.slice(start, end + 1));
      const before = text.slice(0, start).trim();
      const after = text.slice(end + 1).trim();
      return [before, cleanMessageText(parsed), after].filter(Boolean).join("\n\n");
    } catch {}
  }
  return text.replace(/\*\*/g, "").replace(/\n{3,}/g, "\n\n");
}

function sectionIcon(line) {
  const lower = line.toLowerCase();
  if (lower.includes("code review") || lower.includes("feedback")) return CheckCircle2;
  if (lower.includes("improve") || lower.includes("strength")) return Target;
  if (lower.includes("follow-up") || lower.includes("follow up")) return Brain;
  if (lower.includes("communication")) return MessageSquare;
  if (lower.includes("next question")) return ArrowRight;
  return Sparkles;
}

function FormattedMessage({ content, isUser }) {
  const text = cleanMessageText(content);
  const lines = text.split("\n");

  return (
    <div className="space-y-2">
      {lines.map((raw, index) => {
        const line = raw.trim();
        if (!line) return <div key={index} className="h-1" />;

        const isBullet = /^[-•]\s+/.test(line);
        const normalized = line.replace(/^[-•]\s+/, "");
        const isHeading = !isUser && (
          /^[✅🎯🧠💬⚙️💪➡️]/.test(line) ||
          /^(code review|improve next|follow-up question|follow up question|interview communication|next question|feedback|complexity|strengths)$/i.test(line)
        );

        if (isHeading) {
          const Icon = sectionIcon(line);
          return (
            <div key={index} className="pt-1 first:pt-0 flex items-center gap-2 text-[12px] font-bold uppercase tracking-[0.16em] text-indigo-200">
              <Icon className="h-3.5 w-3.5" />
              <span>{line.replace(/^[✅🎯🧠💬⚙️💪➡️]\s*/, "")}</span>
            </div>
          );
        }

        if (isBullet) {
          return (
            <div key={index} className="flex gap-2 text-white/80">
              <CircleDot className="mt-1 h-3 w-3 shrink-0 text-indigo-200/80" />
              <span>{normalized}</span>
            </div>
          );
        }

        return <p key={index} className={isUser ? "text-black" : "text-white/82"}>{line}</p>;
      })}
    </div>
  );
}

function MetricChip({ label, value }) {
  return (
    <div className="px-3 py-1.5 rounded-full border border-white/12 bg-white/5 text-[11px] text-white/75 font-semibold">
      <span className="text-white/50 mr-1">{label}:</span>
      <span className="text-white/85">{value}</span>
    </div>
  );
}

export default function ChatPanel({
  title = "Interview Chat",
  subtitle = "Realtime analysis + scoring",
  messages = [],
  input,
  setInput,
  onSend,
  onEnd,
  isLoading,
  metrics,
  disabled = false,
}) {
  const scrollerRef = useRef(null);

  useEffect(() => {
    if (!scrollerRef.current) return;
    scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
  }, [messages?.length, isLoading]);

  const canSend = useMemo(() => {
    return !!input?.trim() && !isLoading && !disabled;
  }, [input, isLoading, disabled]);

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (canSend) onSend();
    }
  };

  return (
    <div className="h-full flex flex-col rounded-3xl border border-white/10 bg-white/[0.04] backdrop-blur-2xl overflow-hidden shadow-[0_18px_50px_-22px_rgba(0,0,0,0.75)]">
      {onEnd && (
        <button
          type="button"
          onClick={onEnd}
          disabled={disabled}
          title="End interview"
          aria-label="End interview"
          className="group fixed right-5 top-5 z-[9990] inline-flex h-12 w-12 items-center justify-center rounded-2xl border border-rose-300/20 bg-rose-500/15 text-rose-100 shadow-[0_18px_45px_-24px_rgba(244,63,94,0.9)] backdrop-blur-2xl transition-all duration-200 hover:w-44 hover:bg-rose-500/25 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Square className="h-4 w-4 shrink-0 fill-current" />
          <span className="ml-0 max-w-0 overflow-hidden whitespace-nowrap text-sm font-bold opacity-0 transition-all duration-200 group-hover:ml-2 group-hover:max-w-[120px] group-hover:opacity-100">
            End interview
          </span>
        </button>
      )}

      {/* Header */}
      <div className="px-5 py-4 border-b border-white/10 bg-gradient-to-b from-white/[0.06] to-transparent flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <div className="h-9 w-9 rounded-2xl border border-white/12 bg-white/6 flex items-center justify-center shrink-0">
            <Sparkles className="h-4 w-4 text-white/80" />
          </div>
          <div className="leading-tight min-w-0">
            <div className="text-sm font-semibold text-white/85 truncate">{title}</div>
            <div className="text-[11px] text-white/55 truncate">{subtitle}</div>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <MetricChip label="Correctness" value={`${metrics?.correctness ?? 0}/100`} />
          <MetricChip label="Clarity" value={metrics?.clarity ?? "Medium"} />

        </div>
      </div>

      {/* Messages */}
      <div ref={scrollerRef} className="flex-1 min-h-0 overflow-y-auto px-5 py-4 space-y-3">
        {messages.map((m) => {
          const isUser = m.role === "user";
          return (
            <div key={m.id} className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
              <div
                className={[
                  "max-w-[88%] rounded-3xl px-4 py-3 text-sm leading-relaxed",
                  isUser ? "bg-white text-black" : "bg-white/6 border border-white/10 text-white/85",
                ].join(" ")}
              >
                <FormattedMessage content={m.content} isUser={isUser} />
              </div>
            </div>
          );
        })}

        {isLoading && (
          <div className="flex justify-start">
            <div className="max-w-[88%] rounded-3xl px-4 py-3 text-sm bg-white/6 border border-white/10 text-white/70 flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              Thinking…
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      <div className="p-4 border-t border-white/10">
        <div className="relative">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKeyDown}
            rows={2}
            disabled={disabled}
            placeholder="Answer like it’s a real interview…"
            className="w-full resize-none rounded-3xl border border-white/12 bg-white/5 px-4 py-3 pr-14 text-sm text-white/85 outline-none placeholder:text-white/35 focus:border-white/20 disabled:opacity-50"
          />
          <button
            onClick={onSend}
            disabled={!canSend}
            className="absolute right-2.5 bottom-2.5 h-10 w-10 rounded-2xl bg-white text-black flex items-center justify-center disabled:opacity-50 hover:bg-white/90 transition"
            title="Send"
          >
            <Send className="h-4 w-4" />
          </button>
        </div>
        <div className="mt-2 text-[11px] text-white/45">Enter to send • Shift+Enter for new line</div>
      </div>
    </div>
  );
}
